import { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Send, Paperclip, Loader2, FileText, Download, StickyNote, CheckSquare, Gavel, MessageSquare } from 'lucide-react';
import { fmtDateTime } from '../../lib/utils';
import { HtmlView } from '../RichTextEditor';

const KINDS = [
  { value: 'comment', label: 'Comment', icon: MessageSquare, cls: 'border-slate-200 bg-white' },
  { value: 'note', label: 'Meeting Note', icon: StickyNote, cls: 'border-blue-200 bg-blue-50' },
  { value: 'action_item', label: 'Action Item', icon: CheckSquare, cls: 'border-amber-200 bg-amber-50' },
  { value: 'decision', label: 'Decision', icon: Gavel, cls: 'border-emerald-200 bg-emerald-50' },
];
const kindOf = (v) => KINDS.find((k) => k.value === v) || KINDS[0];

export default function EventWorkspace({ eventId, open, onOpenChange, users }) {
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState('');
  const [kind, setKind] = useState('comment');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => eventId && api.get(`/calendar/${eventId}/detail`).then((r) => setDetail(r.data)).catch(() => onOpenChange(false));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, eventId]);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/calendar/${eventId}/comments`, { content: msg, kind });
      setMsg(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (msg.trim()) form.append('note', msg);
      await api.post(`/calendar/${eventId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg(''); toast.success('File attached'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const dl = async (att) => {
    try {
      const r = await api.get(att.file_path.replace('/api', ''), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = att.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const ev = detail?.event;
  const attendeeNames = ev ? users.filter((u) => (ev.attendees || []).includes(u.id)).map((u) => u.full_name) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="event-workspace">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">{ev?.title || 'Meeting Workspace'}</DialogTitle>
          <DialogDescription>
            {ev && <>{fmtDateTime(ev.start_at)}{attendeeNames.length > 0 && <> · Attendees: {attendeeNames.join(', ')}</>}</>}
          </DialogDescription>
        </DialogHeader>
        {ev?.description && <div className="text-sm text-slate-600 -mt-2"><HtmlView html={ev.description} testId="event-desc-view" /></div>}

        <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1" data-testid="event-thread">
          {(detail?.comments || []).length === 0 && <div className="text-center text-sm text-slate-400 py-6">No notes yet — add meeting notes, action items, or decisions below.</div>}
          {(detail?.comments || []).map((c) => {
            const K = kindOf(c.kind === 'file' ? 'comment' : c.kind);
            const Icon = K.icon;
            return (
              <div key={c.id} className={`border rounded-xl p-3 ${c.kind === 'file' ? 'border-slate-200 bg-white' : K.cls}`} data-testid={`event-entry-${c.id}`}>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <Icon className="w-3.5 h-3.5 text-slate-500" />
                  <span className="font-semibold text-navy">{c.user_name}</span>
                  {c.kind !== 'comment' && c.kind !== 'file' && <span className="text-[10px] font-bold uppercase text-slate-500">{kindOf(c.kind).label}</span>}
                  <span className="text-slate-400 ml-auto">{fmtDateTime(c.created_at)}</span>
                </div>
                {c.content && <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.content}</p>}
                {c.attachments?.map((a) => (
                  <button key={a.id} onClick={() => dl(a)} className="mt-1.5 flex items-center gap-2 bg-slate-100 hover:bg-magenta/10 hover:text-magenta rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 transition">
                    <FileText className="w-3.5 h-3.5" /> {a.file_name} <Download className="w-3 h-3 opacity-60" />
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 items-end border-t border-slate-100 pt-3">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-36 shrink-0 h-9" data-testid="event-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={1} placeholder="Add a note, action item, or decision…" className="resize-none min-h-[36px]" data-testid="event-input" />
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.zip,.csv,.txt" onChange={(e) => upload(e.target.files?.[0])} data-testid="event-file-input" />
          <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="event-attach"><Paperclip className="w-4 h-4" /></Button>
          <Button size="icon" onClick={send} disabled={busy || !msg.trim()} className="bg-magenta hover:bg-magenta/90" data-testid="event-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
